// /settings/offline — что StudyHub держит на устройстве (LEARNING-SYSTEM §6.6).
//
// Две разные сущности, которые раньше сливались в одну строку «Занято 0 МБ»:
//   1. Учебная база — PowerSync SQLite: конспекты, карточки, тесты, планы.
//      Скачивается сама, места занимает реально (её и видит пользователь).
//   2. Аудио-резюме — единственный артефакт ВНЕ PowerSync (бинарь на S3),
//      поэтому у него собственный кэш и собственные тумблеры.
// Строка «Занято» считала только (2) и честно показывала 0 МБ, пока ни одно
// аудио не скачано — выглядело как поломка. Теперь показываем обе величины.
//
// Тумблеры пишутся в `users` через PowerSync и РЕАЛЬНО влияют: их читает
// хук предзагрузки (hooks/use-audio-offline-prefetch.ts). «Очистить»
// работает поверх audioOfflineCache (lib/audio-offline-cache.ts).

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, Switch } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconCloudDownload,
	IconDatabase,
	IconHeadphones,
	IconPhoto,
	IconTrash,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { SettingsRow, SettingsSection } from "@/components/settings/settings-shell";
import { SyncIssuesCenter } from "@/components/settings/sync-issues-center";
import { useSession } from "@/hooks/use-session";
import { audioOfflineCache } from "@/lib/audio-offline-cache";
import { imageOfflineCache } from "@/lib/image-offline-cache";
import { useFeatureFlag } from "@/lib/observability/feature-flags";
import { getPowerSync } from "@/lib/powersync/client";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

const searchSchema = z.object({
	demo: z.enum(["default", "loading", "empty", "error"]).optional(),
});

export const Route = createFileRoute("/settings/offline")({
	validateSearch: tolerantSearch(searchSchema),
	component: OfflineSettingsRoute,
	pendingComponent: OfflineSettingsPending,
});

interface LocalOfflineRow {
	audio_offline_prefetch: number | null;
	audio_offline_wifi_only: number | null;
}

/** «—» пока размер не измерен, иначе локализованные мегабайты. */
function useByteFormatter(locale: string) {
	return (bytes: number | null): string => {
		if (bytes == null) return "—";
		return new Intl.NumberFormat(locale, {
			style: "unit",
			unit: "megabyte",
			maximumFractionDigits: 1,
		}).format(bytes / (1024 * 1024));
	};
}

/**
 * Размер локальной SQLite (PowerSync).
 *
 * `page_count * page_size` — единственная величина, доступная одинаково и в
 * web-сборке (wa-sqlite поверх IndexedDB/OPFS), и в Capacitor (@powersync/capacitor + community sqlite).
 * Если pragma-функции недоступны — падаем на StorageManager.estimate(), он
 * считает всё хранилище origin'а (грубее, но не врёт нулём).
 */
async function localDatabaseBytes(): Promise<number | null> {
	try {
		const row = await getPowerSync().getOptional<{ bytes: number | null }>(
			`SELECT (SELECT * FROM pragma_page_count()) * (SELECT * FROM pragma_page_size()) AS bytes`,
		);
		if (row?.bytes && row.bytes > 0) return row.bytes;
	} catch {
		// pragma-функции могут быть выключены в сборке SQLite — идём в фолбэк.
	}
	try {
		const estimate = await navigator.storage?.estimate?.();
		return estimate?.usage ?? null;
	} catch {
		return null;
	}
}

function OfflineSettingsPending(): React.ReactElement {
	const { t } = useTranslation("settings");
	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("offline.title")}
				description={t("offline.subtitle")}
				icon={<IconCloudDownload stroke={1.75} />}
			/>
			<div className="h-40 animate-pulse rounded-[var(--radius-card)] bg-muted mx-4 lg:mx-0" />
		</SettingsPageContainer>
	);
}

function OfflineSettingsRoute(): React.ReactElement {
	const { t, i18n } = useTranslation("settings");
	const { user } = useSession();
	const userId = user?.id ?? "";
	const syncRecoveryCenterEnabled = useFeatureFlag("sync-recovery-center-enabled");

	const query = usePowerSyncQuery<LocalOfflineRow>(
		`SELECT audio_offline_prefetch, audio_offline_wifi_only FROM users WHERE id = ? LIMIT 1`,
		[userId],
	);
	const row = query.data?.[0];

	const [prefetch, setPrefetch] = useState(true);
	const [wifiOnly, setWifiOnly] = useState(true);
	const hydrated = useRef(false);

	useEffect(() => {
		if (!row || hydrated.current) return;
		hydrated.current = true;
		setPrefetch(row.audio_offline_prefetch !== 0);
		setWifiOnly(row.audio_offline_wifi_only !== 0);
	}, [row]);

	function persist(field: "audio_offline_prefetch" | "audio_offline_wifi_only", next: boolean) {
		if (!userId) return;
		void getPowerSync()
			.execute(`UPDATE users SET ${field} = ?, updated_at = ? WHERE id = ?`, [
				next ? 1 : 0,
				new Date().toISOString(),
				userId,
			])
			.catch(() => {
				if (field === "audio_offline_prefetch") setPrefetch(!next);
				else setWifiOnly(!next);
				toast.error(t("offline.saveError"));
			});
	}

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("offline.title")}
				description={t("offline.subtitle")}
				icon={<IconCloudDownload stroke={1.75} />}
			/>

			<LocalDataSection locale={i18n.language} />

			{syncRecoveryCenterEnabled ? <SyncIssuesCenter /> : null}

			<ImageCacheSection locale={i18n.language} />

			<SettingsSection
				title={t("offline.audio.title")}
				description={t("offline.audio.description")}
				icon={<IconHeadphones stroke={1.75} />}
			>
				<SettingsRow
					label={t("offline.audioPrefetch.title")}
					description={t("offline.audioPrefetch.description")}
				>
					<Switch
						checked={prefetch}
						onCheckedChange={(v) => {
							setPrefetch(v);
							persist("audio_offline_prefetch", v);
						}}
					/>
				</SettingsRow>
				<SettingsRow
					label={t("offline.wifiOnly.title")}
					description={t("offline.wifiOnly.description")}
					className={prefetch ? undefined : "pointer-events-none opacity-50"}
				>
					<Switch
						checked={wifiOnly}
						disabled={!prefetch}
						onCheckedChange={(v) => {
							setWifiOnly(v);
							persist("audio_offline_wifi_only", v);
						}}
					/>
				</SettingsRow>
				<OfflineUsageRow locale={i18n.language} />
			</SettingsSection>
		</SettingsPageContainer>
	);
}

/** «Учебная база — N МБ»: то, что PowerSync уже скачал в локальную SQLite. */
function LocalDataSection({ locale }: { locale: string }): React.ReactElement {
	const { t } = useTranslation("settings");
	const format = useByteFormatter(locale);
	const [bytes, setBytes] = useState<number | null>(null);

	useEffect(() => {
		let alive = true;
		void localDatabaseBytes().then((value) => {
			if (alive) setBytes(value);
		});
		return () => {
			alive = false;
		};
	}, []);

	return (
		<SettingsSection
			title={t("offline.localData.title")}
			description={t("offline.localData.description")}
			icon={<IconDatabase stroke={1.75} />}
		>
			<SettingsRow
				label={t("offline.localData.rowLabel")}
				description={t("offline.localData.rowDescription")}
			>
				<span className="text-sm tabular-nums text-muted-foreground">{format(bytes)}</span>
			</SettingsRow>
		</SettingsSection>
	);
}

/** Кэш иллюстраций материалов — отдельно от SQLite и аудио. */
function ImageCacheSection({ locale }: { locale: string }): React.ReactElement {
	const { t } = useTranslation("settings");
	const format = useByteFormatter(locale);
	const [bytes, setBytes] = useState<number | null>(null);
	const [clearing, setClearing] = useState(false);
	const [cacheVersion, setCacheVersion] = useState(0);

	useEffect(() => imageOfflineCache.subscribe(() => setCacheVersion((v) => v + 1)), []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: cacheVersion — намеренный триггер
	useEffect(() => {
		let alive = true;
		void imageOfflineCache.usageBytes().then((value) => {
			if (alive) setBytes(value);
		});
		return () => {
			alive = false;
		};
	}, [cacheVersion]);

	async function onClear() {
		setClearing(true);
		try {
			await imageOfflineCache.clearAll();
			setBytes(0);
			toast.success(t("offline.images.cleared"));
		} catch {
			toast.error(t("offline.images.clearError"));
		} finally {
			setClearing(false);
		}
	}

	return (
		<SettingsSection
			title={t("offline.images.title")}
			description={t("offline.images.description")}
			icon={<IconPhoto stroke={1.75} />}
		>
			<SettingsRow
				label={t("offline.images.usage", { size: format(bytes ?? 0) })}
				description={t("offline.images.hint")}
			>
				<Button
					variant="outline"
					size="sm"
					className="cursor-pointer pointer-coarse:min-h-11"
					disabled={clearing || (bytes ?? 0) === 0}
					onClick={() => void onClear()}
				>
					<IconTrash size={14} stroke={1.75} />
					{t("offline.clear")}
				</Button>
			</SettingsRow>
		</SettingsSection>
	);
}

/** «Занято N МБ · Очистить». Объём читается из адаптера кэша, не из догадок. */
function OfflineUsageRow({ locale }: { locale: string }): React.ReactElement {
	const { t } = useTranslation("settings");
	const format = useByteFormatter(locale);
	const [bytes, setBytes] = useState<number | null>(null);
	const [clearing, setClearing] = useState(false);
	// Кэш меняется и извне (предзагрузка, вытеснение LRU) — подписываемся,
	// чтобы строка не врала после фонового скачивания.
	const [cacheVersion, setCacheVersion] = useState(0);

	useEffect(() => audioOfflineCache.subscribe(() => setCacheVersion((v) => v + 1)), []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: cacheVersion — намеренный триггер перечитывания объёма (в теле не используется)
	useEffect(() => {
		let alive = true;
		void audioOfflineCache.usageBytes().then((value) => {
			if (alive) setBytes(value);
		});
		return () => {
			alive = false;
		};
	}, [cacheVersion]);

	async function onClear() {
		setClearing(true);
		try {
			await audioOfflineCache.clearAll();
			setBytes(0);
			toast.success(t("offline.cleared"));
		} catch {
			toast.error(t("offline.clearError"));
		} finally {
			setClearing(false);
		}
	}

	return (
		<SettingsRow
			label={t("offline.usage", { size: format(bytes ?? 0) })}
			description={t("offline.unavailable")}
		>
			<Button
				variant="outline"
				size="sm"
				className="cursor-pointer pointer-coarse:min-h-11"
				disabled={clearing || (bytes ?? 0) === 0}
				onClick={() => void onClear()}
			>
				<IconTrash size={14} stroke={1.75} />
				{t("offline.clear")}
			</Button>
		</SettingsRow>
	);
}
